import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { CheckCircle, WarningCircle, ArrowsClockwise, LinkBreak, LinkSimple, Lightning } from "@phosphor-icons/react";
import { fmtDate } from "../lib/dateUtil";

const STATUS = {
  active: { label: "Connected — taking payments", cls: "bg-emerald-600 text-white" },
  onboarding: { label: "Setup not finished", cls: "bg-amber-500 text-white" },
  restricted: { label: "Stripe needs more information", cls: "bg-amber-500 text-white" },
};

export default function StripeConnectCard() {
  const [params, setParams] = useSearchParams();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async (refresh) => {
    try {
      if (refresh) {
        const { data } = await API.get("/settings/stripe/connect/status", { params: { refresh: 1 } });
        setS(data.settings);
      } else {
        const { data } = await API.get("/settings/stripe");
        setS(data);
      }
    } catch { toast.error("Failed to load Stripe Connect status"); }
  }, []);

  const start = useCallback(async () => {
    setBusy("start");
    try {
      const { data } = await API.post("/settings/stripe/connect/start", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Stripe onboarding");
      setBusy("");
      load(false);
    }
  }, [load]);

  useEffect(() => {
    const flag = params.get("connect");
    if (flag === "return") {
      load(true).then(() => toast.success("Welcome back from Stripe — status refreshed"));
      params.delete("connect"); setParams(params, { replace: true });
    } else if (flag === "refresh") {
      params.delete("connect"); setParams(params, { replace: true });
      start();
    } else {
      load(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (on) => {
    setBusy(on ? "enable" : "disable");
    try {
      const { data } = await API.post(`/settings/stripe/connect/${on ? "enable" : "disconnect"}`);
      setS(data);
      toast.success(on ? "Stripe Connect switched on" : "Stripe Connect switched off — invoices fall back to your own keys or bank transfer");
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(""); }
  };

  const refresh = async () => {
    setBusy("refresh");
    await load(true);
    setBusy("");
  };

  if (!s) return null;
  const c = s.connect;
  const unavailable = s.connect_available === false;
  const st = c ? STATUS[c.status] || STATUS.onboarding : null;

  return (
    <div className="max-w-2xl rounded-lg border border-border p-4 space-y-3" data-testid="stripe-connect-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold flex items-center gap-2"><Lightning size={16} weight="duotone" className="text-primary" /> Connect with Stripe <Badge variant="outline" className="text-[10px]">Recommended</Badge></div>
          <p className="text-[11px] text-muted-foreground mt-1">
            One click, no keys to copy. Stripe walks you through creating or linking your account; payouts go straight to your bank.
            {s.platform_fee_pct > 0 && <> A {s.platform_fee_pct}% platform fee is deducted from each card payment (shown on your Card Payments page).</>}
          </p>
        </div>
        {c && <Badge className={st.cls} data-testid="stripe-connect-status">{c.enabled ? st.label : "Switched off"}</Badge>}
      </div>

      {c ? (
        <div className="space-y-2" data-testid="stripe-connect-details">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant="secondary" data-testid="stripe-connect-account">{c.name || c.account_id}{c.country ? ` · ${c.country}` : ""}{c.currency ? ` · ${c.currency.toUpperCase()}` : ""}</Badge>
            {c.charges_enabled ? (
              <span className="flex items-center gap-1 text-emerald-700"><CheckCircle size={13} weight="fill" /> Charges enabled</span>
            ) : (
              <span className="flex items-center gap-1 text-amber-700" data-testid="stripe-connect-incomplete"><WarningCircle size={13} weight="fill" /> Charges not enabled yet{c.requirements_due ? ` — ${c.requirements_due} item(s) outstanding` : ""}</span>
            )}
            {c.payouts_enabled && <span className="text-emerald-700">· Payouts enabled</span>}
            {c.last_checked_at && <span className="text-muted-foreground">· Checked {fmtDate(c.last_checked_at)}</span>}
          </div>
          {c.last_error && <p className="text-[11px] text-red-600" data-testid="stripe-connect-error">{c.last_error}</p>}
          <div className="flex flex-wrap gap-2">
            {!c.charges_enabled && (
              <Button size="sm" onClick={start} disabled={!!busy} data-testid="stripe-connect-continue-btn">{busy === "start" ? "Opening Stripe…" : "Continue setup on Stripe"}</Button>
            )}
            <Button size="sm" variant="outline" onClick={refresh} disabled={!!busy} className="gap-1" data-testid="stripe-connect-refresh-btn">
              <ArrowsClockwise size={13} className={busy === "refresh" ? "animate-spin" : ""} /> Refresh status
            </Button>
            {c.enabled ? (
              <Button size="sm" variant="ghost" className="text-red-600 gap-1" onClick={() => toggle(false)} disabled={!!busy} data-testid="stripe-connect-disconnect-btn"><LinkBreak size={13} /> Switch off</Button>
            ) : (
              <Button size="sm" variant="ghost" className="gap-1" onClick={() => toggle(true)} disabled={!!busy} data-testid="stripe-connect-enable-btn"><LinkSimple size={13} /> Switch back on</Button>
            )}
            {c.charges_enabled && (
              <a href="https://dashboard.stripe.com/" target="_blank" rel="noreferrer" className="text-xs text-primary underline self-center" data-testid="stripe-connect-dashboard-link">Open Stripe dashboard</a>
            )}
          </div>
        </div>
      ) : unavailable ? (
        <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 px-3 py-2 text-xs text-amber-800 dark:text-amber-300 flex flex-wrap items-center justify-between gap-2" data-testid="stripe-connect-unavailable">
          <span>One-click Connect isn't switched on for the platform yet — paste your own Stripe API keys below instead.</span>
          <Button size="sm" variant="outline" onClick={start} disabled={!!busy} className="h-7 text-xs" data-testid="stripe-connect-retry-btn">
            {busy === "start" ? "Checking…" : "Try again"}
          </Button>
        </div>
      ) : (
        <Button onClick={start} disabled={!!busy} className="gap-2" data-testid="stripe-connect-start-btn">
          <Lightning size={15} weight="fill" /> {busy === "start" ? "Opening Stripe…" : "Connect with Stripe"}
        </Button>
      )}
    </div>
  );
}
